import React from 'react';

interface ImageViewerToolbarProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomHome: () => void;
  onZoomToLevel: (level: number) => void;
  onToggleFullscreen: () => void;
  isFullscreen: boolean;
  className?: string;
}

export function ImageViewerToolbar({
  onZoomIn,
  onZoomOut,
  onZoomHome,
  onZoomToLevel,
  onToggleFullscreen,
  isFullscreen,
  className = ''
}: ImageViewerToolbarProps) {
  
  const zoomPresets = [
    { level: 0.5, label: '0.5x' },
    { level: 1, label: '1x' },
    { level: 2, label: '2x' },
    { level: 5, label: '5x' },
    { level: 10, label: '10x' },
    { level: 20, label: '20x' },
    { level: 40, label: '40x' }
  ];

  const toolbarItems = [
    {
      icon: '🏠',
      label: 'Αρχική θέση',
      onClick: onZoomHome,
      shortcut: '0'
    },
    {
      icon: '➕',
      label: 'Μεγέθυνση',
      onClick: onZoomIn,
      shortcut: '+'
    },
    {
      icon: '➖',
      label: 'Σμίκρυνση',
      onClick: onZoomOut,
      shortcut: '-'
    },
    {
      icon: isFullscreen ? '⛶' : '⛶',
      label: isFullscreen ? 'Έξοδος από πλήρη οθόνη' : 'Πλήρης οθόνη',
      onClick: onToggleFullscreen,
      shortcut: 'F',
      isToggled: isFullscreen
    }
  ];

  return (
    <div className={`absolute top-20 left-4 z-20 ${className}`}>
      <div className="bg-black bg-opacity-80 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-2">
        {/* Main toolbar buttons */}
        <div className="flex flex-col space-y-1 mb-2">
          {toolbarItems.map((item, index) => (
            <button
              key={index}
              onClick={item.onClick}
              title={`${item.label} (${item.shortcut})`}
              className={`
                w-12 h-12 flex items-center justify-center rounded-md
                text-white hover:bg-white/20 transition-all duration-200
                touch-manipulation min-h-[44px] min-w-[44px]
                ${item.isToggled ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-white/10'}
              `}
            >
              <span className="text-lg">{item.icon}</span>
            </button>
          ))}
        </div>

        {/* Zoom preset buttons */}
        <div className="border-t border-white/20 pt-2">
          <div className="text-xs text-gray-300 text-center mb-2">Προκαθορισμένα Zoom</div>
          <div className="grid grid-cols-2 gap-1">
            {zoomPresets.map((preset) => (
              <button
                key={preset.level}
                onClick={() => onZoomToLevel(preset.level)}
                title={`Zoom στο ${preset.label}`}
                className="
                  px-2 py-1 text-xs font-medium rounded
                  bg-gray-700 hover:bg-gray-600 text-white
                  transition-colors duration-200
                  touch-manipulation min-h-[32px]
                "
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Quick actions section */}
        <div className="border-t border-white/20 pt-2 mt-2">
          <div className="text-xs text-gray-300 text-center mb-2">Γρήγορες Ενέργειες</div>
          
          {/* Fit to screen button */}
          <button
            onClick={() => onZoomToLevel(1)}
            title="Προσαρμογή στην οθόνη"
            className="
              w-full px-2 py-2 text-xs font-medium rounded mb-1
              bg-blue-700 hover:bg-blue-600 text-white
              transition-colors duration-200
              touch-manipulation min-h-[36px]
            "
          >
            📐 Προσαρμογή
          </button>

          {/* Download button (placeholder for future feature) */}
          <button
            onClick={() => {
              // Placeholder for download functionality
              console.log('Download feature not implemented yet');
            }}
            title="Λήψη εικόνας"
            className="
              w-full px-2 py-2 text-xs font-medium rounded
              bg-green-700 hover:bg-green-600 text-white
              transition-colors duration-200
              touch-manipulation min-h-[36px]
            "
          >
            💾 Λήψη
          </button>
        </div>
      </div>

      {/* Mobile-optimized floating toolbar for small screens */}
      <div className="md:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-30">
        <div className="bg-black bg-opacity-90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
          <div className="flex items-center space-x-3">
            <button
              onClick={onZoomOut}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors touch-manipulation"
            >
              <span className="text-lg">➖</span>
            </button>
            
            <button
              onClick={onZoomHome}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors touch-manipulation"
            >
              <span className="text-lg">🏠</span>
            </button>
            
            <button
              onClick={onZoomIn}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors touch-manipulation"
            >
              <span className="text-lg">➕</span>
            </button>

            <div className="w-px h-6 bg-white/30"></div>

            <button
              onClick={onToggleFullscreen}
              className={`
                w-10 h-10 flex items-center justify-center rounded-full text-white transition-colors touch-manipulation
                ${isFullscreen ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white/20 hover:bg-white/30'}
              `}
            >
              <span className="text-lg">⛶</span>
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard shortcuts help */}
      {!isFullscreen && (
        <div className="hidden lg:block mt-4 bg-black bg-opacity-60 backdrop-blur-sm rounded-lg p-3 text-xs text-gray-300 max-w-xs">
          <div className="font-medium mb-2">Πλήκτρα συντόμευσης:</div>
          <div className="space-y-1 text-xs">
            <div><kbd className="bg-gray-700 px-1 rounded">ESC</kbd> Κλείσιμο</div>
            <div><kbd className="bg-gray-700 px-1 rounded">←/→</kbd> Πλοήγηση</div>
            <div><kbd className="bg-gray-700 px-1 rounded">+/-</kbd> Μεγέθυνση</div>
            <div><kbd className="bg-gray-700 px-1 rounded">0</kbd> Αρχική θέση</div>
            <div><kbd className="bg-gray-700 px-1 rounded">F</kbd> Πλήρης οθόνη</div>
            <div><kbd className="bg-gray-700 px-1 rounded">Scroll</kbd> Zoom με ροδάκι</div>
            <div><kbd className="bg-gray-700 px-1 rounded">Drag</kbd> Μετακίνηση</div>
            <div><kbd className="bg-gray-700 px-1 rounded">Double-click</kbd> Zoom</div>
            <div><kbd className="bg-gray-700 px-1 rounded">Pinch</kbd> Zoom σε touch</div>
          </div>
        </div>
      )}
    </div>
  );
}